import { z } from 'zod';
import { ServiceIdSchema } from './common';

export const CallModeSchema = z.enum(['ANONYMOUS', 'STANDARD']);
export type CallMode = z.infer<typeof CallModeSchema>;

export const BookingStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

/**
 * Manual-capture flow (ADR-010): PENDING → AUTHORIZED (client completed
 * Stripe Checkout, funds held) → CAPTURED (therapist accepted) or
 * AUTHORIZATION_CANCELLED (therapist rejected, or the 72h response window
 * expired — zero cost either way). REFUNDED only applies to an
 * already-CAPTURED payment that's later reversed. FAILED is a genuine
 * card/authorization failure.
 */
export const PaymentStatusSchema = z.enum(['PENDING', 'AUTHORIZED', 'CAPTURED', 'REFUNDED', 'FAILED', 'AUTHORIZATION_CANCELLED']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

/** Client-facing booking summary. Money as strings — Decimal is not JSON-native. */
export const BookingSchema = z.object({
  id: z.string().cuid(),
  clientId: z.string().cuid(),
  therapistId: z.string().cuid(),
  serviceId: ServiceIdSchema.nullable(),
  serviceName: z.string().nullable(),
  callMode: CallModeSchema,
  scheduledAt: z.string().datetime(),
  durationMinutes: z.number().int().positive(),
  clientTimezone: z.string(),
  status: BookingStatusSchema,
  paymentStatus: PaymentStatusSchema,
  currency: z.string().length(3),
  grossPrice: z.string(),          // e.g. "60.00"
  platformFeeAmount: z.string(),
  therapistNetAmount: z.string(),
  createdAt: z.string().datetime(),
});
export type Booking = z.infer<typeof BookingSchema>;

/**
 * Payload for POST /api/v1/bookings (Module 5).
 * `serviceId` picks a specific catalog Service (from the therapist's active
 * TherapistService offers) — price and duration are derived server-side
 * from it, never trusted from the client.
 */
export const CreateBookingSchema = z.object({
  therapistId: z.string().cuid(),
  serviceId: ServiceIdSchema,
  scheduledAt: z.string().datetime(),
  callMode: CallModeSchema.default('ANONYMOUS'),
  clientTimezone: z.string().default('Europe/Athens'),
});
export type CreateBookingInput = z.infer<typeof CreateBookingSchema>;

/** GET /api/v1/bookings/my — either the client's own bookings, or (for a
 *  therapist account) bookings made with them. Role is read from the JWT. */
export const MyBookingsResponseSchema = z.object({
  bookings: z.array(BookingSchema),
});
export type MyBookingsResponse = z.infer<typeof MyBookingsResponseSchema>;
